import { Bell, CalendarDays, CircleDollarSign, Home, Images, Users, TrendingUp, UserRound } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { AppExperience } from "@/app/types";

export type AppTab = "home" | "students" | "agenda" | "finance" | "evolution" | "moments" | "notifications" | "profile";

type NavigationItem = { key: AppTab; label: string; Icon: LucideIcon };

/** Navigation model: resolve contexto + capabilities antes da camada visual. */
export function getBottomNavigationItems(experience: AppExperience, capabilities: string[] = []): NavigationItem[] {
  const teaching = experience === "Professor" || experience === "Monitor";
  const agendaLabel = teaching ? "Agenda" : experience === "Gestor" ? "Aulas" : "Agenda";
  const canGestorStudents = capabilities.includes("alunos.visualizar");
  const canGestorFinance = capabilities.includes("financeiro.visualizar");
  const canGestorOperation = capabilities.includes("presenca.visualizar") && capabilities.includes("turmas.visualizar") && capabilities.includes("equipe.visualizar");

  return teaching
    ? [
        { key:"home" as const, label:"Hoje", Icon:Home },
        { key:"agenda" as const, label:agendaLabel, Icon:CalendarDays },
        { key:"evolution" as const, label:"Evolução", Icon:TrendingUp },
        { key:"moments" as const, label:"Momentos", Icon:Images },
        { key:"profile" as const, label:"Perfil", Icon:UserRound },
      ]
    : experience === "Gestor"
      ? [
          { key:"home" as const, label:"Hoje", Icon:Home },
          ...(canGestorStudents ? [{ key:"students" as const, label:"Alunos", Icon:Users }] : []),
          ...(canGestorFinance ? [{ key:"finance" as const, label:"Financeiro", Icon:CircleDollarSign }] : []),
          ...(canGestorOperation ? [{ key:"agenda" as const, label:"Operação", Icon:CalendarDays }] : []),
          { key:"profile" as const, label:"Perfil", Icon:UserRound },
        ]
      : experience === "Aluno"
        ? [
            { key:"home" as const, label:"Início", Icon:Home },
            { key:"agenda" as const, label:agendaLabel, Icon:CalendarDays },
            { key:"evolution" as const, label:"Evolução", Icon:TrendingUp },
            { key:"moments" as const, label:"Momentos", Icon:Images },
            { key:"profile" as const, label:"Perfil", Icon:UserRound },
          ]
        : [
            { key:"home" as const, label:"Início", Icon:Home },
            { key:"agenda" as const, label:agendaLabel, Icon:CalendarDays },
            { key:"notifications" as const, label:"Notificações", Icon:Bell },
            { key:"profile" as const, label:"Perfil", Icon:UserRound },
          ];
}

export function BottomNav({ experience, active, capabilities = [], onNavigate }: { experience: AppExperience; active: AppTab; capabilities?: string[]; onNavigate: (tab: AppTab) => void }) {
  const teaching = experience === "Professor" || experience === "Monitor";
  const items = getBottomNavigationItems(experience, capabilities);
  return <nav aria-label="Navegação do App" className="app-bottom-nav safe-bottom fixed inset-x-0 bottom-0 z-30 px-4 pb-2"><div style={{gridTemplateColumns:`repeat(${items.length}, minmax(0, 1fr))`}} className={`nav-glass mx-auto grid max-w-lg ${teaching ? "grid-cols-5" : "grid-cols-4"} ${experience === "Gestor" ? "grid-cols-5" : ""} gap-1 rounded-[20px] px-2 pt-1.5`}>{items.map(item=><Nav key={item.key} label={item.label} Icon={item.Icon} active={active===item.key} onClick={()=>onNavigate(item.key)}/>)}</div></nav>;
}
function Nav({label,Icon,active,onClick}:{label:string;Icon:LucideIcon;active:boolean;onClick:()=>void}) {
  return <button type="button" onClick={onClick} className={`relative flex min-h-[62px] flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-medium ${active?"bg-teal/[.07] text-teal-light":"text-white/65"}`} aria-current={active?"page":undefined}>{active&&<span aria-hidden="true" className="absolute -top-1.5 h-[2px] w-10 rounded-full bg-teal"/>}<Icon aria-hidden="true" className="h-[22px] w-[22px]"/><span>{label}</span></button>;
}
